import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Button, Input, Select } from "antd";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const COLOR = "#06b6d4";

const CONTACT_FIELDS = [
  { label: "First Name",   value: "firstname" },
  { label: "Last Name",    value: "lastname" },
  { label: "Email",        value: "email" },
  { label: "Phone",        value: "phone" },
  { label: "Company",      value: "company" },
  { label: "Designation",  value: "designation" },
  { label: "Status",       value: "status" },
  { label: "Notes",        value: "notes" },
  { label: "Website",      value: "website" },
  { label: "LinkedIn",     value: "linkedin" },
  { label: "Instagram",    value: "instagram" },
];

const SOURCE_OPTIONS = [
  { label: "Static text",       value: "static" },
  { label: "Contact field",     value: "contact" },
  { label: "Capture input answer", value: "answer" },
];

const SetVariableNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();

  const update = (patch) => data.onChange?.({ ...data, ...patch });

  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 260,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={COLOR} />
      <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>📝</span>
        <strong style={{ fontSize: 13 }}>Set Variable</strong>
        <Button type="text" size="small" icon={<CopyOutlined />}
          onClick={() => data.onDuplicate?.()}
          style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />}
          onClick={() => deleteElements({ nodes: [{ id }] })}
          style={{ color: "#9ca3af" }} />
      </div>

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>Variable name</div>
          <Input
            value={data.varName || ""}
            onChange={(e) => update({ varName: e.target.value })}
            size="small"
            placeholder="e.g. plan_type"
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>Value from</div>
          <Select
            value={data.valueSource || "static"}
            onChange={(v) => update({ valueSource: v, value: "", contactField: "", answerNodeId: "" })}
            size="small"
            style={{ width: "100%" }}
            options={SOURCE_OPTIONS}
          />
        </div>

        {(!data.valueSource || data.valueSource === "static") && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>Value</div>
            <Input
              value={data.value || ""}
              onChange={(e) => update({ value: e.target.value })}
              size="small"
              placeholder="e.g. premium"
            />
          </div>
        )}

        {data.valueSource === "contact" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>Contact field</div>
            <Select
              value={data.contactField || undefined}
              onChange={(v) => update({ contactField: v })}
              size="small"
              style={{ width: "100%" }}
              options={CONTACT_FIELDS}
              placeholder="Select field"
            />
          </div>
        )}

        {data.valueSource === "answer" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>Answer node ID</div>
            <Input
              value={data.answerNodeId || ""}
              onChange={(e) => update({ answerNodeId: e.target.value })}
              size="small"
              placeholder="Paste the Capture Input node ID"
            />
            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>Find node ID in the URL when editing</div>
          </div>
        )}

        {data.varName && (
          <div style={{ fontSize: 10, color: COLOR, padding: "3px 6px", background: `${COLOR}11`, borderRadius: 4 }}>
            Saves permanently to contact as <code>{"{{" + (data.varName || "…") + "}}"}</code>
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} style={{ background: COLOR }} />
    </div>
  );
};

export default SetVariableNode;
